import { useState, useCallback, useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { IlamyCalendar, CalendarEvent, EventFormProps } from '@ilamy/calendar'
import dayjs, { Dayjs } from 'dayjs'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { Label } from '@/components/ui/label'
import { BookingForm } from './BookingForm'
import { buildPausaEvents, envelopeBusinessHours } from './pausaEvents'
import { lockedCalendarDragMessage } from './lockedDrag'
import { collapseOverlappingBookings, overlapGroupTitle } from './overlapGroups'
import { calendarEventClick } from './eventClick'
import { bookingChipTitle } from './bookingChipTitle'
import type { 
  PageProps, 
  CalendarBooking, 
  CalendarStaff, 
  CalendarService, 
  CalendarSpace,
  BusinessHours 
} from '@/types'

interface CalendarBusiness {
  id: number
  name: string
}

interface CalendarIndexProps {
  current_user: PageProps['current_user']
  current_organization: PageProps['current_organization']
  current_business: PageProps['current_business']
  bookings: CalendarBooking[]
  staff: CalendarStaff[]
  services: CalendarService[]
  spaces: CalendarSpace[]
  business_hours: BusinessHours[] | null
  businesses: CalendarBusiness[]
  is_admin?: boolean
  can_create?: boolean
  can_edit?: boolean
  can_delete?: boolean
  current_staff_id?: number | null
}

export default function CalendarIndex({
  bookings: initialBookings,
  staff,
  services,
  spaces,
  business_hours,
  businesses = [],
  is_admin = false,
  can_create = false,
  can_edit = false,
  current_staff_id,
  current_user,
  current_organization,
  current_business,
}: CalendarIndexProps) {
  const orgSlug = current_organization?.slug || ''
  const hasPackagePrefill =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('client_package_id')
  const [bookings, setBookings] = useState<CalendarBooking[]>(initialBookings)
  const [selectedStaffId, setSelectedStaffId] = useState<number | 'all'>('all')
  const [overlapGroup, setOverlapGroup] = useState<CalendarBooking[] | null>(null)
  const [selectedOverlapBooking, setSelectedOverlapBooking] = useState<CalendarBooking | null>(null)

  const visibleBookings = bookings.filter(
    b => selectedStaffId === 'all' || b.staff_assignment_id === selectedStaffId,
  )
  const displayItems = collapseOverlappingBookings(visibleBookings)
  const bookingEvents: CalendarEvent[] = displayItems.map(item => {
    if (item.kind === 'group') {
      return {
        id: `overlap:${item.bookings.map(booking => booking.id).join(',')}`,
        title: overlapGroupTitle(item.bookings.length),
        start: dayjs(item.start),
        end: dayjs(item.end),
        backgroundColor: '#27272a',
        color: '#fafafa',
        data: { event_type: 'overlap_group', bookings: item.bookings },
      }
    }
    const booking = item.booking
    return {
      id: booking.id,
      title: bookingChipTitle(booking),
      start: dayjs(booking.start),
      end: dayjs(booking.end),
      backgroundColor: booking.backgroundColor,
      color: booking.color,
      data: booking,
    }
  })

  const pausaEvents = buildPausaEvents(
    business_hours,
    dayjs().subtract(1, 'month').startOf('month'),
    dayjs().add(1, 'month').endOf('month'),
  )
  const calendarEvents: CalendarEvent[] = [...bookingEvents, ...pausaEvents]

  const businessHoursConfig = envelopeBusinessHours(business_hours)?.map(bh => ({
    daysOfWeek: bh.daysOfWeek as ('monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday')[],
    startTime: bh.startTime,
    endTime: bh.endTime,
  }))

  const handleEventAdd = useCallback(async (_event: CalendarEvent) => {
    if (!can_edit) return
    // This is handled by the custom form
  }, [can_edit])

  const handleEventUpdate = useCallback(async (event: CalendarEvent) => {
    const lockedMessage = lockedCalendarDragMessage(event.data?.event_type)
    if (lockedMessage) {
      toast.error(lockedMessage)
      setBookings((prev) => prev.slice())
      return
    }

    try {
      const start = (event.start as Dayjs).toISOString()
      const end = (event.end as Dayjs).toISOString()
      
      const response = await fetch(`/${orgSlug}/calendar/bookings/${event.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({
          booking: {
            starts_at: start,
            ends_at: end,
          }
        }),
      })

      if (response.ok) {
        const data = await response.json()
        setBookings(prev => prev.map(b => b.id === data.booking.id ? data.booking : b))
      } else {
        const data = await response.json().catch(() => null)
        const errors = data?.errors
        const rawMessage = errors
          ? Object.values(errors).flat().join(', ')
          : ''

        if (rawMessage.includes('disponible') || rawMessage.includes('availability') || rawMessage.includes('horario')) {
          const staffName = event.data?.staff_name
          toast.error(staffName
            ? `${staffName} no está disponible a esa hora.`
            : 'No se pudo actualizar la cita.')
        }
        // Refetch to revert visual change
        const refetch = await fetch(`/${orgSlug}/calendar.json`)
        if (refetch.ok) {
          const fresh = await refetch.json()
          setBookings(fresh.bookings)
        }
      }
    } catch (error) {
      console.error('Failed to update booking:', error)
    }
  }, [orgSlug])

  const handleEventDelete = useCallback(async (event: CalendarEvent) => {
    if (event.data?.event_type === 'time_block' || event.data?.event_type === 'pausa' || event.data?.event_type === 'overlap_group') return

    try {
      await fetch(`/${orgSlug}/calendar/bookings/${event.id}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
      })
      setBookings(prev => prev.filter(b => b.id !== event.id))
    } catch (error) {
      console.error('Failed to delete booking:', error)
    }
  }, [orgSlug])

  const handleBookingCreated = useCallback((booking: CalendarBooking) => {
    setBookings(prev => [...prev, booking])
  }, [])

  const handleBookingUpdated = useCallback((booking: CalendarBooking) => {
    setBookings(prev => prev.map(b => b.id === booking.id ? booking : b))
  }, [])

  // Compute business hours range for TimePicker
  const businessHoursRange = business_hours && business_hours.length > 0
    ? {
        startHour: Math.min(...business_hours.map(bh => bh.startTime)),
        endHour: Math.max(...business_hours.map(bh => bh.endTime)),
      }
    : null

  const canEditBooking = useCallback((booking?: CalendarBooking) => {
    if (!can_edit) return false
    if (booking?.event_type === 'time_block' || booking?.event_type === 'pausa' || booking?.editable === false) return false
    if (is_admin) return true
    if (!booking) return true // creating new
    return booking.staff_assignment_id === current_staff_id
  }, [can_edit, is_admin, current_staff_id])

  const renderBookingEditor = useCallback((
    props: EventFormProps,
    resolvedBooking?: CalendarBooking,
  ) => {
    const canEdit = canEditBooking(resolvedBooking)

    if (!canEdit && (resolvedBooking || props.selectedEvent?.id)) {
      // Read-only view for someone else's booking
      if (!props.open) return null
      const b = resolvedBooking
      return (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={props.onClose}
        >
          <div
            className="w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">{
                b?.event_type === 'pausa'
                  ? 'Pausa'
                  : b?.event_type === 'time_block'
                    ? 'Bloque ocupado'
                    : 'Detalle de cita'
              }</h2>
              <button onClick={props.onClose} className="text-muted-foreground hover:text-foreground">✕</button>
            </div>
            <div className="space-y-3">
              {b?.event_type !== 'time_block' && b?.event_type !== 'pausa' && (
                <p><span className="text-muted-foreground text-sm">Cliente:</span> {b?.client_name || 'Sin asignar'}</p>
              )}
              {b?.event_type !== 'pausa' && (
                <p><span className="text-muted-foreground text-sm">Servicio:</span> {b?.service_name}</p>
              )}
              {b?.event_type !== 'pausa' && (
                <p><span className="text-muted-foreground text-sm">Asignado a:</span> {b?.staff_name || 'Sin asignar'}</p>
              )}
              {b?.space_name && (
                <p><span className="text-muted-foreground text-sm">Espacio:</span> {b.space_name}</p>
              )}
              {b?.start && (
                <p><span className="text-muted-foreground text-sm">Inicio:</span> {new Date(b.start).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })}</p>
              )}
              {b?.end && (
                <p><span className="text-muted-foreground text-sm">Fin:</span> {new Date(b.end).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })}</p>
              )}
              {b?.event_type === 'time_block'
                ? b.private_details_visible && b.notes && (
                  <p><span className="text-muted-foreground text-sm">Notas:</span> {b.notes}</p>
                )
                : b?.notes && (
                  <p><span className="text-muted-foreground text-sm">Notas:</span> {b.notes}</p>
                )}
              {b?.covered_by_package && (
                <p className="rounded-md bg-primary/10 px-3 py-2 text-sm text-primary">
                  Cubierta por {b.package_name || "paquete"} ·{" "}
                  {b.package_sessions_available} sesiones disponibles
                </p>
              )}
            </div>
            <p className="text-xs text-muted-foreground italic mt-4">
              {b?.event_type === 'pausa'
                ? 'La sucursal no atiende en este horario.'
                : b?.event_type === 'time_block'
                  ? b.private_details_visible
                    ? 'Solo tú puedes ver el detalle de este evento de Google Calendar. No se edita desde Tippy.'
                    : 'Este bloqueo viene de Google Calendar y no se edita desde Tippy.'
                  : 'Solo puedes editar tus propias citas.'}
            </p>
          </div>
        </div>
      )
    }
    return (
      <BookingForm
        {...props}
        orgSlug={orgSlug}
        staff={staff}
        services={services}
        spaces={spaces}
        businesses={businesses}
        currentBusinessId={current_business?.id ?? null}
        businessHours={businessHoursRange}
        onBookingCreated={handleBookingCreated}
        onBookingUpdated={handleBookingUpdated}
        currentStaffId={current_staff_id}
        canReassignStaff={is_admin}
      />
    )
  }, [orgSlug, staff, services, spaces, businesses, current_business, businessHoursRange, handleBookingCreated, handleBookingUpdated, can_edit, can_create, current_staff_id, canEditBooking])

  const renderEventForm = useCallback((props: EventFormProps) => {
    const eventData = props.selectedEvent?.data as Record<string, any> | undefined
    if (eventData?.event_type === 'overlap_group') return null
    const booking = eventData as CalendarBooking | undefined
    const resolvedBooking = booking?.staff_assignment_id !== undefined
      ? booking
      : bookings.find(b => b.id?.toString() === props.selectedEvent?.id?.toString()) || undefined
    return renderBookingEditor(props, resolvedBooking)
  }, [bookings, renderBookingEditor])

  const translations: Record<string, string> = {
    // Actions
    today: 'Hoy',
    new: 'Nueva cita',
    export: 'Exportar',
    create: 'Crear',
    update: 'Actualizar',
    delete: 'Eliminar',
    cancel: 'Cancelar',
    save: 'Guardar',
    // Views
    month: 'Mes',
    week: 'Semana',
    day: 'Día',
    year: 'Año',
    // Time
    allDay: 'Todo el día',
    more: 'más',
    // Months
    january: 'enero',
    february: 'febrero',
    march: 'marzo',
    april: 'abril',
    may: 'mayo',
    june: 'junio',
    july: 'julio',
    august: 'agosto',
    september: 'septiembre',
    october: 'octubre',
    november: 'noviembre',
    december: 'diciembre',
    // Days
    sunday: 'domingo',
    monday: 'lunes',
    tuesday: 'martes',
    wednesday: 'miércoles',
    thursday: 'jueves',
    friday: 'viernes',
    saturday: 'sábado',
  }

  const calendarContainerRef = useRef<HTMLDivElement>(null)
  const [calendarView, setCalendarView] = useState<string>('week')

  // Auto-scroll week/day views to 1 hour before business hours open.
  //
  // Why this is complex:
  // - @ilamy/calendar doesn't expose a scrollToTime prop
  // - The calendar uses Radix ScrollArea, so we need to find the inner viewport element
  // - Views are swapped with framer-motion AnimatePresence (mode="wait"), meaning
  //   the old view animates out (~100ms) before the new one mounts
  // - On initial load we poll immediately (no flash of 12am)
  // - On view switch we wait 150ms for the exit animation, then poll for the new viewport
  // - We track the last scrolled element to avoid scrolling the exiting view
  const lastScrolledViewport = useRef<Element | null>(null)
  const isInitialMount = useRef(true)

  useEffect(() => {
    const node = calendarContainerRef.current
    if (!node || !businessHoursRange) return
    if (calendarView !== 'week' && calendarView !== 'day') return

    const scrollTo = Math.max(0, businessHoursRange.startHour - 1) * 60
    lastScrolledViewport.current = null

    let raf: number
    let cancelled = false

    const poll = () => {
      if (cancelled) return
      const selector = calendarView === 'week' ? '[data-testid="week-scroll-area"]' : '[data-testid="day-scroll-area"]'
      const scrollArea = node.querySelector(selector)
      const viewport = scrollArea?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement | null
      if (viewport && viewport.scrollHeight > 0 && viewport !== lastScrolledViewport.current) {
        viewport.scrollTop = scrollTo
        lastScrolledViewport.current = viewport
        return
      }
      raf = requestAnimationFrame(poll)
    }

    if (isInitialMount.current) {
      isInitialMount.current = false
      raf = requestAnimationFrame(poll)
    } else {
      const timeout = setTimeout(() => {
        raf = requestAnimationFrame(poll)
      }, 150)
      return () => {
        cancelled = true
        cancelAnimationFrame(raf)
        clearTimeout(timeout)
      }
    }

    return () => {
      cancelled = true
      cancelAnimationFrame(raf)
    }
  }, [calendarView, businessHoursRange])

  if (!current_user) return null

  return (
    <DashboardLayout
      currentUser={current_user}
      currentOrganization={current_organization}
      currentBusiness={current_business}
      orgSlug={orgSlug}
    >
      <div className="h-full flex flex-col">
        {hasPackagePrefill && (
          <div className="mb-4 rounded-lg bg-primary/10 px-4 py-3 text-sm text-primary">
            Paquete preseleccionado. Elige una fecha y horario para reservar una
            sesión; la cita no se cobrará nuevamente.
          </div>
        )}

        {/* Staff filter */}
        {staff.length > 1 && (
          <div className="mb-4 flex items-center gap-3">
            <Label htmlFor="staff-filter">Ver calendario de:</Label>
            <select
              id="staff-filter"
              className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              value={selectedStaffId}
              onChange={(e) => setSelectedStaffId(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            >
              <option value="all">Todos</option>
              {staff.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Calendar */}
        <div className="flex-1 min-h-0" ref={calendarContainerRef}>
          <IlamyCalendar
            events={calendarEvents}
            initialView="week"
            firstDayOfWeek="monday"
            locale="es"
            timeFormat="12-hour"
            businessHours={businessHoursConfig}
            onEventAdd={(can_edit || can_create) ? handleEventAdd : undefined}
            onEventUpdate={(can_edit || can_create) ? handleEventUpdate : undefined}
            onEventDelete={(can_edit || can_create) ? handleEventDelete : undefined}
            renderEventForm={renderEventForm}
            translations={translations as any}
            disableDragAndDrop={!can_edit && !can_create}
            disableCellClick={!can_edit && !can_create}
            onEventClick={(event) => {
              const action = calendarEventClick<CalendarBooking>(event)
              if (action.kind === 'overlap_group') {
                setSelectedOverlapBooking(null)
                setOverlapGroup(action.bookings)
                return
              }
              if (action.kind === 'open_booking') {
                setOverlapGroup(null)
                setSelectedOverlapBooking(action.booking)
              }
            }}
            onViewChange={(view) => setCalendarView(view)}
          />
        </div>
      </div>
      {overlapGroup && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center"
          onClick={() => setOverlapGroup(null)}
        >
          <div
            className="max-h-[80vh] w-full max-w-md overflow-y-auto rounded-t-2xl border border-border bg-card p-6 shadow-lg sm:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="overlap-title"
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 id="overlap-title" className="text-lg font-semibold">
                {overlapGroupTitle(overlapGroup.length)}
              </h2>
              <button
                type="button"
                onClick={() => setOverlapGroup(null)}
                className="rounded-xl px-2 py-1 text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                ✕
              </button>
            </div>
            <ul className="space-y-2">
              {overlapGroup.map((booking) => (
                <li key={booking.id}>
                  <button
                    type="button"
                    className="flex w-full flex-col rounded-xl border border-border px-4 py-3 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:bg-accent/80"
                    onClick={() => {
                      setSelectedOverlapBooking(booking)
                      setOverlapGroup(null)
                    }}
                  >
                    <span className="font-medium">
                      {booking.client_name || 'Sin asignar'}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {booking.staff_name || 'Sin asignar'}
                      {booking.service_name ? ` · ${booking.service_name}` : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {selectedOverlapBooking && renderBookingEditor({
        open: true,
        selectedEvent: {
          id: selectedOverlapBooking.id,
          title: selectedOverlapBooking.title,
          start: dayjs(selectedOverlapBooking.start),
          end: dayjs(selectedOverlapBooking.end),
          data: selectedOverlapBooking,
        },
        onClose: () => setSelectedOverlapBooking(null),
        onDelete: handleEventDelete,
        onUpdate: handleEventUpdate,
      }, selectedOverlapBooking)}
    </DashboardLayout>
  )
}
